import { ExclamationTriangleIcon } from "@heroicons/react/24/solid"

import type { SolarConsumptionInputMode } from "~/api/dto/solar-proposal-consumption.dto"
import { TextField } from "~/components/ui-components/v2"

import {
  GREEN_BUTTON_OVERRIDE_WARNING,
  MONTH_LABELS,
  formatKwh,
  type ConsumptionDraft,
  type ConsumptionFormState,
} from "./solar-consumption-form"
import { SolarSummaryStepFooter } from "./summary/SolarSummaryStepFooter"

const MODES: ReadonlyArray<{ id: SolarConsumptionInputMode; label: string }> = [
  { id: `annual`, label: `Annual total` },
  { id: `monthly`, label: `Monthly` },
]

/**
 * Edit usage: an annual total or twelve months in kWh, the save error, and the
 * inline confirmation a save over Green Button needs. Holds no state: the step
 * owns the draft and the save.
 */
export const SolarConsumptionEditor = ({
  draft,
  form,
  busy,
  error,
  confirmingOverride,
  onEdit,
  onSave,
  onConfirmOverride,
  onCancelOverride,
  summary,
}: {
  draft: ConsumptionDraft
  form: ConsumptionFormState
  busy: boolean
  error: string | null
  confirmingOverride: boolean
  onEdit: (patch: Partial<ConsumptionDraft>) => void
  onSave: () => void
  onConfirmOverride: () => void
  onCancelOverride: () => void
  /**
   * The Summary's step card: Continue while nothing is edited (enabled when
   * the saved usage already counts), Save & Continue once it is.
   */
  summary?: { canContinue: boolean; onContinue: () => void }
}): JSX.Element => (
  <section
    aria-label="Edit usage"
    className="space-y-4 rounded-xl border border-border bg-surface p-5"
  >
    <h3 className="text-xs font-semibold tracking-wide text-muted uppercase">
      Edit usage
    </h3>
    <div
      role="group"
      aria-label="Usage entry"
      className="inline-flex rounded-lg border border-border bg-background p-1"
    >
      {MODES.map((mode) => (
        <button
          key={mode.id}
          type="button"
          aria-pressed={draft.mode === mode.id}
          disabled={busy}
          className={
            draft.mode === mode.id
              ? `rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground`
              : `rounded-md px-3 py-1.5 text-sm font-semibold text-muted hover:text-foreground`
          }
          onClick={() => onEdit({ mode: mode.id })}
        >
          {mode.label}
        </button>
      ))}
    </div>

    {draft.mode === `annual` ? (
      <TextField
        label="Annual usage (kWh)"
        inputMode="decimal"
        disabled={busy}
        value={draft.annualText}
        description="The months are estimated from this total when you save."
        error={form.errors.annual ?? undefined}
        onChange={(event) => onEdit({ annualText: event.target.value })}
      />
    ) : (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {MONTH_LABELS.map((label, month) => (
            <TextField
              key={label}
              label={`${label} (kWh)`}
              inputMode="decimal"
              disabled={busy}
              value={draft.monthlyTexts[month]}
              error={form.errors.months[month] ?? undefined}
              onChange={(event) =>
                onEdit({
                  monthlyTexts: draft.monthlyTexts.map((text, index) =>
                    index === month ? event.target.value : text,
                  ),
                })
              }
            />
          ))}
        </div>
        <p
          className="text-sm text-muted"
          data-testid="solar-consumption-total-preview"
        >
          Total (preview):{` `}
          <strong className="text-foreground tabular-nums">
            {form.monthlyTotalPreview == null
              ? `—`
              : formatKwh(form.monthlyTotalPreview)}
          </strong>
        </p>
        {form.errors.total ? (
          <p className="text-sm text-danger">{form.errors.total}</p>
        ) : null}
      </div>
    )}

    {error ? (
      <p role="alert" className="text-sm text-danger">
        {error}
      </p>
    ) : null}

    {confirmingOverride ? (
      <div
        role="alert"
        data-testid="solar-consumption-override-warning"
        className="space-y-3 rounded-lg border border-warning/40 bg-warning/10 p-3"
      >
        <p className="flex items-start gap-2 text-sm text-foreground">
          <ExclamationTriangleIcon
            aria-hidden="true"
            className="mt-0.5 size-4 shrink-0 text-warning"
          />
          {GREEN_BUTTON_OVERRIDE_WARNING}
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            disabled={busy}
            onClick={onConfirmOverride}
          >
            Replace Green Button data
          </button>
          <button
            type="button"
            className="text-sm font-semibold text-muted underline hover:text-foreground"
            onClick={onCancelOverride}
          >
            Cancel
          </button>
        </div>
      </div>
    ) : summary ? (
      <SolarSummaryStepFooter
        changed={form.changed}
        busy={busy}
        disabled={form.changed ? !form.valid : !summary.canContinue}
        onSave={onSave}
        onContinue={summary.onContinue}
      />
    ) : (
      <div>
        <button
          type="button"
          className="rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          disabled={!form.changed || !form.valid || busy}
          onClick={onSave}
        >
          {busy ? `Saving…` : `Save`}
        </button>
      </div>
    )}
  </section>
)
